import styled from 'styled-components';
import { exFont } from '../../shared/typography';
import { StepPage, StepHead } from '../YourMarksStep/style';

// Samples (tplA 1117–1144, calMulti) and the graders the drawer reuses.
// Prototype values with no Exams token (tplA 1120–1142).
const TAB_DONE_LINE = '#bfe5cf';
const TAB_DONE_TINT = '#f0faf4';
const TAB_INK = '#3d3847';
const ADD_LINE = '#c9b2f7';
const GUIDE_MARK_TINT = '#f3f0f8';
const REMOVE_LINE = '#f3c4bf';
const WARN_TEXT = '#8a6d00';
// The wizard's sticky header and footer (tplA 413, 1144): Samples fills the
// space between them so the answer and the grader scroll on their own and
// "Next" stays in view, as in the prototype.
const SHELL_HEADER = '66px';
const SHELL_FOOTER = '75px';

export const SamplesPage = styled(StepPage)`
  max-width: none;
  padding: 0;
  gap: 0;
`;

export const SamplesHead = styled(StepHead)`
  align-items: center;
  gap: 14px;
  padding: 14px 24px 0;
  background: var(--ex-surface);
`;

export const Progress = styled.span`
  ${exFont(600, 14)}
  line-height: normal;
  color: var(--ex-ink);
  white-space: nowrap;
`;

export const TabsBar = styled.div`
  padding: 12px 24px 14px;
  background: var(--ex-surface);
  border-bottom: 1px solid var(--ex-line);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const tabTone = ({ $active, $done }) => {
  if ($active)
    return 'border-color: var(--ex-purple); background: var(--ex-plan-tint); color: var(--ex-purple);';
  if ($done)
    return `border-color: ${TAB_DONE_LINE}; background: ${TAB_DONE_TINT}; color: ${TAB_INK};`;
  return `border-color: var(--ex-line-strong); background: var(--ex-surface); color: ${TAB_INK};`;
};

export const QuestionTab = styled.button`
  ${exFont(600, 13.5)}
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1.5px solid;
  ${tabTone}
  white-space: nowrap;
  cursor: pointer;
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const Dots = styled.span`
  display: inline-flex;
  gap: 3px;
`;

// Marked samples: purple on the open tab, green elsewhere (tplA 2631).
export const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $done, $active }) =>
    !$done
      ? 'var(--ex-line-strong)'
      : $active
      ? 'var(--ex-purple)'
      : 'var(--ex-ok)'};
`;

export const AddSample = styled.button`
  ${exFont(600, 13.5)}
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1.5px dashed ${ADD_LINE};
  background: var(--ex-surface);
  color: var(--ex-purple);
  white-space: nowrap;
  cursor: pointer;
  > span {
    font-weight: 500;
    color: var(--ex-ink-2);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
    outline-offset: 2px;
  }
`;

export const Spacer = styled.span`
  flex: 1 1 auto;
`;

// The answer (left, scrolls) and the grader (right; its own scroll above a
// pinned "Your mark · Next" bar), tplA 1123–1142.
export const Workspace = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 400px);
  align-items: stretch;
  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ViewerColumn = styled.div`
  min-height: 0;
  overflow: auto;
  padding: 20px 24px 28px;
  background: var(--ex-surface-2);
`;

export const ViewerInner = styled.div`
  max-width: 820px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const GraderPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  background: var(--ex-surface);
  border-left: 1px solid var(--ex-line);
  @media (max-width: 760px) {
    border-left: 0;
    border-top: 1px solid var(--ex-line);
  }
`;

export const GraderScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--ex-surface);
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-card);
  box-shadow: var(--ex-shadow-1);
  min-width: 0;
`;

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--ex-line-strong);
  border-radius: 50%;
  background: var(--ex-surface);
  color: var(--ex-ink-strong);
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const ViewerHead = styled.div`
  [role='radiogroup'] {
    gap: 0;
    padding: 3px;
    border: 1px solid var(--ex-line-strong);
    border-radius: 999px;
    background: var(--ex-surface);
  }
  [role='radio'] {
    ${exFont(600, 13)}
    padding: 6px 12px;
    border-radius: 999px;
    color: var(--ex-ink-strong);
    box-shadow: none;
  }
  [role='radio'][aria-checked='true'] {
    background: var(--ex-purple);
    color: var(--white);
  }
  ${NavButton} {
    width: 34px;
    height: 34px;
    color: var(--ex-ink);
  }

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

export const ViewerLabel = styled.span`
  ${exFont(700, 12.5)}
  line-height: normal;
  padding: 4px 11px;
  border-radius: 999px;
  color: var(--white);
  background: var(--ex-purple);
`;

export const SampleCount = styled.span`
  ${exFont(600, 14)}
  line-height: normal;
  color: var(--ex-ink);
  white-space: nowrap;
`;

export const SampleScript = styled.span`
  ${exFont(500, 13.5)}
  line-height: normal;
  color: var(--ex-ink-2);
`;

export const RemoveSample = styled.button`
  ${exFont(600, 12.5)}
  line-height: 1;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid ${REMOVE_LINE};
  background: var(--ex-surface);
  color: var(--ex-bad-ink);
  white-space: nowrap;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const QuestionText = styled.div`
  ${exFont(400, 'm')}
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-tint);
  color: var(--ex-ink);
  line-height: 1.5;
  p {
    margin: 0;
  }
`;

export const SampleQuestion = styled(QuestionText)`
  padding: 12px 16px;
  border: 1px solid var(--ex-line);
  border-radius: 12px;
  background: var(--ex-surface);
`;

export const StimulusLine = styled.p`
  ${exFont(500, 12.5)}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const ResponseText = styled.div`
  ${exFont(400, 'm')}
  color: var(--ex-ink);
  line-height: 1.6;
  white-space: pre-wrap;
  p {
    margin: 0 0 4px;
  }
`;

export const PageLabel = styled.figcaption`
  ${exFont(600, 'xs')}
  margin-bottom: 4px;
  color: var(--ex-ink-muted);
`;

export const PageImage = styled.img`
  display: block;
  width: 100%;
  border: 1px solid var(--ex-line);
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-2);
`;

export const Notice = styled.p`
  ${exFont(500, 's')}
  margin: 0;
  padding: 16px;
  border-radius: var(--ex-radius-item);
  background: var(--ex-surface-2);
  color: var(--ex-ink-2);
`;

export const GraderHead = styled.p`
  ${exFont(600, 'm')}
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0 0 8px;
  color: var(--ex-ink);
`;

export const GraderHint = styled.span`
  ${exFont(400, 's')}
  margin: 0;
  color: var(--ex-ink-2);
`;

export const Criterion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid var(--ex-line);
`;

export const CriterionName = styled.p`
  ${exFont(600, 's')}
  margin: 0;
  color: var(--ex-ink-strong);
`;

export const LevelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const LevelButton = styled.button`
  ${exFont(700, 's')}
  min-width: 34px;
  height: 30px;
  padding: 0 8px;
  border-radius: var(--ex-radius-item);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line-strong)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-surface)'};
  color: ${({ $active }) =>
    $active ? 'var(--white)' : 'var(--ex-ink-strong)'};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LevelSelected = styled.p`
  ${exFont(400, 'xs')}
  margin: 0;
  color: var(--ex-ink-2);
  line-height: 1.45;
`;

export const MarkRow = styled.div`
  ${exFont(600, 's')}
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: var(--ex-ink-strong);
`;

export const MarkInput = styled.input`
  ${exFont(600, 'm')}
  width: 64px;
  padding: 6px 8px;
  text-align: center;
  border: 1px solid
    ${({ $over, $warn }) =>
      $over
        ? 'var(--ex-bad)'
        : $warn
        ? 'var(--ex-warn)'
        : 'var(--ex-line-strong)'};
  border-radius: var(--ex-radius-item);
  color: ${({ $over }) => ($over ? 'var(--ex-bad-ink)' : 'var(--ex-ink)')};
  &:focus {
    border-color: var(--ex-purple);
    outline: none;
  }
`;

export const OverMax = styled.span`
  ${exFont(500, 'xs')}
  color: var(--ex-bad-ink);
`;

export const Warning = styled.p`
  ${exFont(500, 's')}
  margin: 8px 0 0;
  padding: 8px 12px;
  border-left: 3px solid var(--ex-warn);
  border-radius: var(--ex-radius-item);
  color: var(--ex-warn-ink);
  background: var(--ex-warn-bg);
`;

export const GuideRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  margin-bottom: 6px;
  padding: 10px 12px;
  text-align: left;
  border-radius: var(--ex-radius-item);
  border: 1px solid
    ${({ $active }) => ($active ? 'var(--ex-purple)' : 'var(--ex-line)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple-100)' : 'var(--ex-surface)'};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid var(--ex-purple);
  }
`;

export const GuideMark = styled.span`
  ${exFont(700, 'm')}
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ex-purple)')};
  background: ${({ $active }) =>
    $active ? 'var(--ex-purple)' : 'var(--ex-purple-100)'};
`;

export const GuideText = styled.span`
  ${exFont(400, 's')}
  color: var(--ex-ink);
  line-height: 1.45;
`;

export const PanelFoot = styled.div`
  margin-top: auto;
  ${exFont(600, 's')}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--ex-line);
  color: var(--ex-ink-strong);
`;

// The selected level's letter in its grade colour (tplA 1131, meta()).
const GRADE_TONES = Object.freeze({
  A: 'var(--ex-ok-action)',
  B: 'var(--ex-grade-b-ink)',
  C: WARN_TEXT,
  D: 'var(--ex-grade-d-ink)',
  E: 'var(--ex-bad-ink)',
});

export const GradeWord = styled.span`
  font-weight: 600;
  color: ${({ $grade }) => GRADE_TONES[$grade] || 'var(--ex-ink)'};
`;

export const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
`;

export const WaitingTitle = styled.p`
  ${exFont(600, 'xl')}
  margin: 0;
`;

export const WaitingBody = styled.p`
  ${exFont(400, 'm')}
  margin: 0;
  max-width: 520px;
  color: var(--ex-ink-2);
  line-height: 1.5;
`;

export const SamplesLayout = styled(SamplesPage)`
  height: calc(100vh - ${SHELL_HEADER} - ${SHELL_FOOTER});
  min-height: 420px;
  h2 {
    ${exFont(700, 21)}
    line-height: normal;
  }
  ${SamplesHead} > div > p {
    ${exFont(400, 14)}
    margin: 3px 0 0;
    line-height: normal;
    color: var(--ex-ink-strong);
  }
  [data-testid='samples-waiting'] {
    margin: 20px 24px;
  }
  ${ViewerColumn} [data-testid='typed-response'] {
    min-height: 240px;
    padding: 24px 28px;
    border: 1px solid var(--ex-line);
    border-radius: 12px;
    background: var(--ex-surface);
    font: 400 16px/1.8 Georgia, 'Times New Roman', serif;
    color: var(--ex-ink);
    p {
      margin: 0 0 14px;
    }
    /* Blank lines between paragraphs: the 14px margin is the gap here. */
    p:has(> br:only-child) {
      display: none;
    }
  }
  ${ViewerColumn} ${SampleQuestion} p {
    ${exFont(400, 14.5)}
    line-height: 1.5;
  }
  ${ViewerColumn} ${PageLabel} {
    ${exFont(500, 11)}
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--ex-ink-faint);
  }

  /* The graders, as Samples shows them (tplA 1128–1139). */
  ${GraderPanel} ${GraderHead} {
    ${exFont(700, 16)}
    margin: 0;
    line-height: normal;
  }
  ${GraderPanel} ${GraderHead} ${GraderHint} {
    ${exFont(400, 13)}
  }
  ${GraderPanel} [data-testid='rubric-grader'],
  ${GraderPanel} [data-testid='guide-grader'] {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  ${GraderPanel} ${Criterion} {
    gap: 0;
    padding: 0;
    border: 0;
  }
  ${GraderPanel} ${CriterionName} {
    ${exFont(600, 13.5)}
    line-height: normal;
    margin: 0 0 6px;
    color: var(--ex-ink);
  }
  ${GraderPanel} ${LevelButton} {
    ${exFont(700, 14)}
    flex: 1 1 0;
    min-width: 0;
    height: 38px;
    border-width: 1.5px;
    border-radius: 10px;
  }
  ${GraderPanel} ${LevelSelected} {
    ${exFont(400, 12.5)}
    margin: 7px 0 0;
    line-height: 1.45;
    color: var(--ex-ink-strong);
  }
  ${GraderPanel} ${MarkRow} {
    gap: 10px;
    margin: 0;
    padding: 12px 14px;
    border: 1.5px solid var(--ex-line);
    border-radius: 12px;
    > span:first-child {
      ${exFont(700, 15)}
      flex: 1;
      color: var(--ex-ink);
    }
    > span:nth-of-type(2) {
      ${exFont(600, 14)}
      color: var(--ex-ink-2);
    }
  }
  ${GraderPanel} ${MarkInput} {
    ${exFont(800, 19)}
    width: 68px;
    height: 44px;
    box-sizing: border-box;
    border-width: 1.5px;
    border-radius: 10px;
  }
  ${GraderPanel} ${Warning} {
    ${exFont(500, 12.5)}
    margin: -6px 0 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: none;
    color: ${WARN_TEXT};
  }
  ${GraderPanel} [data-testid='guide-grader'] > [role='radiogroup'] {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  ${GraderPanel} ${GuideRow} {
    gap: 10px;
    margin: 0;
    border-width: 1.5px;
    border-radius: 10px;
    background: var(--ex-surface);
  }
  ${GraderPanel} ${GuideRow}[aria-checked='true'] {
    background: var(--ex-plan-tint);
  }
  ${GraderPanel} ${GuideMark} {
    ${exFont(700, 15)}
    width: 40px;
    height: 28px;
    border-radius: 8px;
    color: var(--ex-ink);
    background: ${GUIDE_MARK_TINT};
  }
  ${GraderPanel} ${GuideRow}[aria-checked='true'] ${GuideMark} {
    color: var(--white);
    background: var(--ex-purple);
  }
  ${GraderPanel} ${GuideText} {
    ${exFont(400, 13.5)}
    padding-top: 4px;
  }
  ${GraderPanel} [data-testid='guide-grader'] > p${GraderHint} {
    ${exFont(400, 12.5)}
    margin: 0;
    color: var(--ex-ink-2);
  }
  ${GraderPanel} ${PanelFoot} {
    margin: 0;
    padding: 14px 20px;
    border-top: 1px solid var(--ex-line);
    > span {
      ${exFont(600, 14)}
      color: var(--ex-ink);
    }
    button {
      ${exFont(600, 14.5)}
      min-height: 44px;
      padding: 0 22px;
      border: 0;
      border-radius: 999px;
      white-space: nowrap;
    }
    button:disabled {
      opacity: 1;
      color: var(--white);
      background: var(--ex-disabled-bg);
    }
  }

  @media (max-width: 760px) {
    height: auto;
    ${ViewerColumn}, ${GraderScroll} {
      overflow: visible;
    }
  }
`;
